import InvoiceIcons from '@/icons/InvoiceIcons'
import PeopleIcons from '@/icons/PeopleIcons'
import ServiceIcons from '@/icons/ServiceIcons'
import { formatDate } from '@/utils/function'
import { generatePathDatacentral, paths } from '@/utils/path'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Chip } from 'vnetwork-ui'
import PrivacyField from '@/components/PrivacyField'
import {
    CONTRACT_STATUS_CONFIG,
    ContractStatus,
    CUSTOMER_STATUS_CONFIG,
    CustomerStatus,
    INVOICE_STATUS_CONFIG,
    InvoiceStatus,
    SearchResult,
    SearchResultWrapLine,
    SERVICE_STATUS_CONFIG,
    ServiceStatus,
} from '../../types'
import HighlightText from '../HighlightText'

interface SearchResultItemProps {
    result: SearchResult
    query?: string
    onItemClick?: (result: SearchResultWrapLine) => void
}

const SearchResultItem = ({
    result,
    query = '',
    onItemClick,
}: SearchResultItemProps) => {
    const { t } = useTranslation('search')

    const handleWrapLine = (rs: SearchResult): SearchResultWrapLine => {
        switch (rs.type) {
            case 'customer': {
                const status =
                    CUSTOMER_STATUS_CONFIG[rs.customer_status as CustomerStatus]
                return {
                    label: [rs.customer_id, rs?.company_name].join(' - '),
                    subtitle: undefined,
                    redirect_to: generatePathDatacentral(paths.customerDetail, {
                        id: rs.customer_id,
                    }),
                    status: status
                        ? {
                              label: t(status.label),
                              color: status.color,
                          }
                        : undefined,
                    icon: <PeopleIcons className="w-4 h-4 text-primary" />,
                    created_at: rs.created_at,
                    email: rs.email,
                }
            }
            case 'service': {
                const serviceStatus =
                    SERVICE_STATUS_CONFIG[rs.service_status as ServiceStatus]
                return {
                    label: [
                        rs.service_id,
                        rs?.service_name,
                        `(${rs?.entity_name})`,
                    ].join(' - '),
                    subtitle: [rs?.customer_id, rs?.company_name]?.join(' - '),
                    redirect_to: generatePathDatacentral(paths.serviceDetail, {
                        id: rs.service_id,
                    }),
                    status: serviceStatus
                        ? {
                              label: t(serviceStatus.label),
                              color: serviceStatus.color,
                          }
                        : undefined,
                    icon: <ServiceIcons className="w-4 h-4 text-primary" />,
                    created_at: rs.created_at,
                    email: rs.email,
                }
            }
            case 'contract': {
                const contractStatus =
                    CONTRACT_STATUS_CONFIG[
                        rs?.contract_status as ContractStatus
                    ]
                return {
                    label: [
                        rs?.customer_id,
                        rs?.company_name,
                        rs?.contract_number_id,
                    ].join(' - '),
                    subtitle: undefined,
                    redirect_to: generatePathDatacentral(
                        paths.customerContractDetail,
                        {
                            id: rs?.contract_id,
                        }
                    ),
                    status: contractStatus
                        ? {
                              label: t(contractStatus.label),
                              color: contractStatus.color,
                          }
                        : undefined,
                    icon: <InvoiceIcons className="w-4 h-4 text-primary" />,
                    created_at: rs.created_at,
                    email: rs.email,
                }
            }
            case 'invoice': {
                const invoiceStatus = rs.invoice_status
                    ? INVOICE_STATUS_CONFIG[rs.invoice_status as InvoiceStatus]
                    : null
                return {
                    label: [
                        rs?.customer_id,
                        rs?.company_name,
                        rs?.invoice_code,
                    ].join(' - '),
                    subtitle: undefined,
                    redirect_to: generatePathDatacentral(paths.invoiceDetail, {
                        id: rs.invoice_code,
                    }),
                    status: invoiceStatus
                        ? {
                              label: t(invoiceStatus.label),
                              color: invoiceStatus.color,
                          }
                        : undefined,
                    icon: <InvoiceIcons className="w-4 h-4 text-primary" />,
                    created_at: rs.created_at,
                    email: rs.email,
                }
            }
            default:
                return {
                    label: rs.type,
                    created_at: rs.created_at,
                }
        }
    }
    const resultWrapLine = useMemo(() => {
        return handleWrapLine(result)
    }, [result])

    return (
        <div
            onClick={() => onItemClick?.(resultWrapLine)}
            className="p-3 border-b border-gray-200 hover:bg-gray-50 cursor-pointer transition-colors"
        >
            <div className="flex items-start justify-between">
                <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                        {resultWrapLine?.icon && resultWrapLine.icon}
                        <div className="flex-1">
                            <HighlightText
                                text={resultWrapLine.label}
                                searchQuery={query}
                                className="font-medium text-sm text-primary hover:underline line-clamp-1 uppercase"
                                highlightClassName="bg-yellow-200 font-semibold"
                            />
                        </div>
                    </div>
                    <div className="text-xs text-gray-600 flex flex-col gap-1">
                        {resultWrapLine?.subtitle && (
                            <HighlightText
                                text={resultWrapLine.subtitle}
                                searchQuery={query}
                                className="text-gray-600"
                                highlightClassName="bg-yellow-200 font-medium"
                            />
                        )}
                        {resultWrapLine?.email && (
                            <div className="mt-1">
                                <PrivacyField
                                    value={resultWrapLine.email}
                                    type="email"
                                    className="text-xs"
                                />
                            </div>
                        )}
                    </div>
                </div>
                <div className="text-xs text-gray-400 ml-4 m flex items-center gap-1">
                    {resultWrapLine?.status && (
                        <Chip
                            label={resultWrapLine.status.label}
                            color={resultWrapLine.status.color as any}
                            size="small"
                            variant="contained"
                            className="mr-4"
                        />
                    )}
                    {formatDate(result.created_at)}
                </div>
            </div>
        </div>
    )
}

export default SearchResultItem
